/*
 * Session allocation tab (PROD-9977): who joins the last 10 minutes of each 1-on-1
 * trial running now or starting in the next 4 hours. Session-only: it never
 * assigns the task or the advisor.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import { Btn, Chip, LABEL_CLASS, SalesDialog } from '../ui';
import {
  BOARD_POLL_MS,
  allocatedByLine,
  cantJoinLabel,
  fillMessage,
  fmtTime,
  groupSessions,
  levelLabel,
  liveStatus,
  pickerOptions,
  repLoads,
  rowWarnings,
  savedMessage,
  staleMessage,
  studentLocal,
  summarize,
  updatedLabel,
  windowLine
} from './allocation';
import type { PickOption } from './allocation';
import {
  errorReason,
  errorStatus,
  fetchBoard,
  fillEmptySlots,
  saveAllocation,
  staleAllocation
} from './api';
import RepPicker from './RepPicker';
import { sessionHref } from './join_now';
import type { AllocSession, AllocationBoard } from './types';

const CLOCK_TICK_MS = 30 * 1000;

type Notify = (type: 'success' | 'error', message: string) => void;

interface Pending {
  session: AllocSession;
  option: PickOption;
}

const CONFIRM_BODY_ID = 'session-alloc-confirm-body';

function SessionAllocationPanel({ notify }: { notify: Notify }) {
  const [board, setBoard] = useState<AllocationBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [loadedAt, setLoadedAt] = useState(0);
  const [skew, setSkew] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [filling, setFilling] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const seqRef = useRef(0);
  const loadedAtRef = useRef(0);
  // After a 403 the polls stop until the tab is opened again (a fresh mount).
  const forbiddenRef = useRef(false);

  const load = useCallback(() => {
    seqRef.current += 1;
    const seq = seqRef.current;
    setLoading(true);
    return fetchBoard()
      .then((b) => {
        if (seq !== seqRef.current) return;
        const server = Date.parse(b.now);
        // Group labels follow the server clock, not a drifting laptop clock.
        setSkew(Number.isNaN(server) ? 0 : server - Date.now());
        setBoard(b);
        setLoadError('');
        setForbidden(false);
        loadedAtRef.current = Date.now();
        setLoadedAt(loadedAtRef.current);
      })
      .catch((e: unknown) => {
        if (seq !== seqRef.current) return;
        if (errorStatus(e) === 403) {
          forbiddenRef.current = true;
          setForbidden(true);
        } else setLoadError(errorReason(e, 'Could not load session allocation'));
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
  }, []);

  // Polls only while the browser tab is visible; catches up on return.
  useEffect(() => {
    load();
    const poll = () => {
      if (document.visibilityState === 'hidden' || forbiddenRef.current) return;
      load();
    };
    const onVisible = () => {
      if (document.visibilityState === 'hidden' || forbiddenRef.current) return;
      if (Date.now() - loadedAtRef.current >= BOARD_POLL_MS) load();
    };
    const id = setInterval(poll, BOARD_POLL_MS);
    const tick = setInterval(() => setClock(Date.now()), CLOCK_TICK_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      seqRef.current += 1;
      clearInterval(id);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const now = clock + skew;
  const sessions = useMemo(() => board?.sessions || [], [board]);
  const reps = useMemo(() => board?.reps || [], [board]);
  const groups = useMemo(() => groupSessions(sessions, now), [sessions, now]);
  const summary = useMemo(() => summarize(sessions, now), [sessions, now]);
  const loads = useMemo(() => repLoads(reps, sessions), [reps, sessions]);
  const canEdit = !!board?.can_edit;

  const save = (s: AllocSession, opsUuid: string, repName: string) => {
    setSaving((p) => ({ ...p, [s.session_uuid]: true }));
    saveAllocation(s.session_uuid, opsUuid, s.allocation?.version || 0)
      .then(() => notify('success', savedMessage(s, repName)))
      .catch((e: unknown) => {
        const current = staleAllocation(e);
        if (current !== undefined) notify('error', staleMessage(s.student_name, current));
        else notify('error', errorReason(e, 'Could not save the allocation'));
      })
      .finally(() => {
        setSaving((p) => {
          const next = { ...p };
          delete next[s.session_uuid];
          return next;
        });
        load();
      });
  };

  const pick = (s: AllocSession, option: PickOption | null) => {
    if (!option) {
      if (s.allocation?.ops_uuid) save(s, '', '');
      return;
    }
    if (option.selected) return;
    if (option.confirm) {
      setPending({ session: s, option });
      return;
    }
    save(s, option.rep.ops_uuid, option.rep.name);
  };

  const confirmPending = () => {
    if (!pending) return;
    setPending(null);
    save(pending.session, pending.option.rep.ops_uuid, pending.option.rep.name);
  };

  const fill = () => {
    setFilling(true);
    fillEmptySlots()
      .then((r) => notify('success', fillMessage(r)))
      .catch((e: unknown) => notify('error', errorReason(e, 'Could not fill empty slots')))
      .finally(() => {
        setFilling(false);
        load();
      });
  };

  if (forbidden) {
    return (
      <div className="mx-[5%] my-5 rounded-[10px] border border-line-subtle bg-surface-card px-5 py-8 text-center text-[13px] text-content-secondary max-sm:mx-3">
        Session allocation is for super users, sales managers and L2 or L3 reps.
      </div>
    );
  }

  const renderRow = (s: AllocSession) => {
    const live = liveStatus(s, now);
    const local = studentLocal(s.student_tz, s.start_at);
    const warnings = rowWarnings(s, now);
    const cantJoin = cantJoinLabel(s);
    const pickerId = `alloc-pick-${s.session_uuid}`;
    return (
      <div
        key={s.session_uuid}
        data-testid={`alloc-row-${s.session_uuid}`}
        className="grid grid-cols-1 gap-3 border-t border-line-subtle px-3.5 py-2.5 first:border-t-0 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.3fr)] sm:items-start"
      >
        <div className="flex min-w-0 flex-col gap-[3px]">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/studentprofile?id=${encodeURIComponent(s.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] font-semibold text-content-link hover:underline"
            >
              {s.student_name || 'Student'}
            </Link>
            <Chip tone={live.tone}>{live.label}</Chip>
            <Link
              to={sessionHref(s.session_uuid)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-content-link hover:underline"
            >
              Session
            </Link>
          </div>
          <span className="text-xs text-content-secondary">
            {fmtTime(s.start_at)} to {fmtTime(s.end_at)} IST
            {local ? ` · student time ${local}` : ''}
          </span>
          <span className="text-xs text-content-muted">
            Teacher {s.teacher_name || '—'} · advisor {s.advisor_name || 'none yet'}
          </span>
        </div>
        <div className="flex flex-col gap-[3px] text-xs text-content-secondary">
          <span>
            Join at <b className="font-semibold text-content-primary">{fmtTime(s.join_at)}</b>
          </span>
          <span className="text-content-muted">{allocatedByLine(s.allocation)}</span>
          {s.task?.claimed_by_name ? (
            <span className="text-content-muted">Task held by {s.task.claimed_by_name}</span>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span id={`${pickerId}-label`} className={LABEL_CLASS}>
            Joins last 10 min
          </span>
          <RepPicker
            id={pickerId}
            labelId={`${pickerId}-label`}
            allocation={s.allocation}
            options={pickerOptions(s, reps, sessions)}
            disabled={!canEdit}
            busy={!!saving[s.session_uuid]}
            onPick={(o) => pick(s, o)}
          />
          {cantJoin ? (
            <span className="self-start">
              <Chip tone="error">{cantJoin}</Chip>
            </span>
          ) : null}
          {warnings.map((w) => (
            <span
              key={w}
              className="self-start rounded-md px-2 py-[3px] text-[11.5px] font-semibold"
              style={{ background: 'var(--state-warning-bg)', color: 'var(--state-warning-text)' }}
            >
              {w}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-[5%] my-5 flex flex-col gap-3.5 text-[13px] text-content-primary max-sm:mx-3">
      <section
        aria-label="Session allocation summary"
        className="flex flex-wrap items-center gap-4 rounded-[10px] border border-line-subtle bg-surface-card px-3.5 py-3 max-sm:flex-col max-sm:items-stretch max-sm:gap-2"
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="m-0 text-[15px] font-bold leading-5">
            1-on-1 trial sessions, running now and the next 4 hours
          </h2>
          <span className="text-xs text-content-secondary" data-testid="alloc-summary">
            {windowLine(now)} · {summary.total} {summary.total === 1 ? 'session' : 'sessions'} ·{' '}
            <b className="font-semibold">{summary.allocated} allocated</b> ·{' '}
            <b className="font-semibold text-state-error-text">{summary.need} need someone</b> ·
            next join {summary.next_join}
          </span>
        </div>
        <span className="flex-1 max-sm:hidden" />
        <div className="flex items-center gap-2.5 max-sm:justify-between">
          <span className="text-[11.5px] text-content-muted" aria-live="polite">
            {loading ? 'Updating…' : updatedLabel(loadedAt, clock)}
          </span>
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            aria-label="Refresh session allocation"
            className="max-sm:!h-11 max-sm:!w-11"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 34,
              width: 34,
              borderRadius: 9999,
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-card)',
              cursor: loading ? 'wait' : 'pointer',
              padding: 0,
              margin: 0
            }}
          >
            <FaSyncAlt
              size={13}
              color="var(--text-link-brand)"
              className={loading ? 'animate-spin' : ''}
            />
          </button>
        </div>
        <Btn
          variant="primary"
          onClick={fill}
          disabled={!canEdit || filling || summary.need === 0}
          className="max-sm:!h-11 max-sm:!w-full"
          title={summary.need === 0 ? 'Every session has someone' : undefined}
        >
          {filling ? 'Filling…' : 'Fill empty slots'}
        </Btn>
      </section>

      {loadError ? (
        <div
          role="alert"
          className="rounded-lg px-3 py-2 text-xs"
          style={{ background: 'var(--state-error-bg)', color: 'var(--state-error-text)' }}
        >
          {loadError}
        </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-3.5">
        <div className="flex min-w-0 flex-col gap-3.5" style={{ flex: '999 1 640px' }}>
          {board && groups.length === 0 ? (
            <div className="rounded-[10px] border border-line-subtle bg-surface-card px-5 py-8 text-center text-content-secondary">
              No 1-on-1 trial sessions running now or in the next 4 hours.
            </div>
          ) : null}
          {!board && !loadError ? (
            <div className="rounded-[10px] border border-line-subtle bg-surface-card px-5 py-8 text-center text-xs text-content-muted">
              Loading sessions…
            </div>
          ) : null}
          {groups.map((g) => (
            <section
              key={g.key}
              aria-label={g.title}
              className="overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card"
            >
              <div className="flex flex-wrap items-center gap-2.5 border-b border-line-subtle bg-surface-row-muted px-3.5 py-2.5 max-sm:flex-col max-sm:items-start max-sm:gap-1">
                <h3 className="m-0 text-[13px] font-bold">{g.title}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone={g.chip.tone}>{g.chip.label}</Chip>
                  <span className="text-xs text-content-muted">Join window {g.join_window}</span>
                </div>
              </div>
              {g.sessions.map(renderRow)}
            </section>
          ))}
        </div>

        <aside
          aria-label="Who is on shift"
          className="flex min-w-0 flex-col gap-2.5 rounded-[10px] border border-line-subtle bg-surface-card px-3.5 py-3"
          style={{ flex: '1 1 300px' }}
        >
          <h3 className={`m-0 ${LABEL_CLASS}`}>On shift</h3>
          {loads.length === 0 ? (
            <span className="text-xs text-content-muted">No L2 or L3 rep is on shift.</span>
          ) : null}
          {loads.map((l) => (
            <div
              key={l.rep.ops_uuid}
              className="flex flex-col gap-1 border-t border-line-subtle py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 rounded-sm"
                  style={{
                    background: l.rep.level === 'L3' ? 'var(--state-warning)' : 'var(--state-info)'
                  }}
                />
                <span className="text-[13px] font-semibold">{l.rep.name}</span>
                <span className="text-[11px] text-content-muted">
                  {levelLabel(l.rep.level)} · {l.shift}
                </span>
              </div>
              <span className="text-xs text-content-secondary">{l.line}</span>
            </div>
          ))}
          <span className="text-[11.5px] leading-[1.45] text-content-muted">
            Fill empty slots picks L3 first, then L2 backup, on shift at the join time and not
            already joining another session then. Anyone can change a pick.
          </span>
        </aside>
      </div>
      <span className="text-[11.5px] text-content-muted">
        Allocating a session does not assign its task or change the student&apos;s advisor.
      </span>

      <SalesDialog
        open={!!pending}
        onClose={() => setPending(null)}
        role="alertdialog"
        title={pending?.option.confirm?.title || ''}
        describedBy={CONFIRM_BODY_ID}
        testId="alloc-confirm"
        footer={
          <>
            <Btn variant="neutral" onClick={() => setPending(null)}>
              Pick someone else
            </Btn>
            <Btn variant="primary" onClick={confirmPending}>
              Allocate anyway
            </Btn>
          </>
        }
      >
        <div
          id={CONFIRM_BODY_ID}
          className="px-4 py-3.5 text-[13px] leading-[1.5] text-content-secondary"
        >
          {pending?.option.confirm?.body}
        </div>
      </SalesDialog>
    </div>
  );
}

export default SessionAllocationPanel;
